import {
  EmployeeLiteModel,
  EmployeeModel,
  getEmployeeKey,
  getEmployeesKey,
  useGetEmployee,
  useGetEmployees,
} from 'api-hooks/employee';
import { OnChangeParams, Option } from 'baseui/select';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { QueryTransformer } from 'common/helpers/common';
import { Input } from 'components/elements';
import { useAuthorization } from 'hooks/use-authorization';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(item: EmployeeLiteModel | EmployeeModel): Option {
  return {
    id: item.id,
    label: item.name,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value?: EmployeeModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
}

export default function EmployeeSelectInput(props: Props) {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<any>();
  const {
    label = t('common:employee'),
    placeholder = t('common:select_employee'),
  } = props;
  const value = useWatch({ control, name: props.name });

  const { navigate } = useNavigation();

  const { can } = useAuthorization();

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetEmployees,
    useDetailLazyQueryHook: useGetEmployee,
    listKeyFunction: getEmployeesKey,
    detailKeyFunction: getEmployeeKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
        },
      }),
      [],
    ),
    renderCreate: !!can(AuthorizationRules.EmployeesCreate),
    onClickCreate: () => {
      navigate(NavigationRoutes.EmployeesCreate);
      props.onClose?.();
    },
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, EmployeeModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled}
      overrides={overrides}
      {...selectProps}
    />
  );
}
